import React, { useRef, useState } from 'react'
import { isEmpty, isEqual } from 'lodash'
import pluralize from 'pluralize'

import { showResult } from '@/utils/helpers'

import { MultiSelect } from '../../forms/MultiSelect'
import { FilterColumnButton } from '../FilterColumnButton'
import { FilterPill } from '../FilterPill'
import { FilterTypePopup } from './FilterTypePopup'
import { getDynamicOptions } from '../filter.helpers'

export const FilterTypeMultiSelect = ({
  autoFocus = true,
  schema = {},
  rows,
  value,
  loading,
  updateSearch,
  columnAccessor,
  onClose,
  onRemove,
  customFilterIcon,
  ...props
}) => {
  const [open, setOpen] = useState(false)
  const [localValue, setLocalValue] = useState(value)
  const valueOnOpenRef = useRef(value)
  const options = schema.options || getDynamicOptions(rows, schema)

  const displayValue = open ? localValue : value
  const resolvedAutoFocus = schema.autoFocus !== undefined ? schema.autoFocus : autoFocus
  const resolvedPosition = schema.popupPosition || (columnAccessor ? 'top left' : 'bottom left')
  const applyOnChange = schema.applyOnChange || false

  const handleSetOpen = (isOpen) => {
    if (isOpen) {
      valueOnOpenRef.current = value
      setLocalValue(value)
    }

    setOpen(isOpen)

    if (!isOpen && !applyOnChange && !isEqual(localValue, valueOnOpenRef.current)) {
      updateSearch(schema.accessor, localValue)

      if (onClose) {
        onClose()
      }
    }
  }

  return (
    <FilterTypePopup
      open={open}
      setOpen={handleSetOpen}
      position={resolvedPosition}
      trigger={
        columnAccessor ? (
          <FilterColumnButton
            type="multiSelect"
            columnAccessor={columnAccessor}
            value={displayValue}
            hasValue={!isEmpty(displayValue)}
            displayLabel={showResult(displayValue, schema.label)}
          />
        ) : (
          <FilterPill
            clickable
            isClearable={!isEmpty(value)}
            handleRemoveFilter={(event) => {
              event.stopPropagation()

              if (onRemove) {
                onRemove(schema.accessor)
              } else {
                updateSearch(schema.accessor, [])
              }
            }}
            loading={loading}
            label={schema.label}
            value={showResult(displayValue, null)}
            active={open}
            customFilterIcon={customFilterIcon}
          />
        )
      }
    >
      <MultiSelect
        autoFocus={resolvedAutoFocus}
        menuIsOpen
        isSelectAll={schema.isSelectAll ?? !schema.options}
        showFullOptions={false}
        condenseOptions
        placeholderPill={`All ${pluralize(schema.label)}`}
        label={schema.label}
        options={options}
        optionsLength={schema.options ? undefined : options.length}
        loading={loading}
        value={localValue}
        // The pill popup is a floating-ui popover: a body-portaled menu renders outside
        // it (misplaced, since the popover is positioned after mount) and option clicks
        // count as outside presses that dismiss the popup before onChange fires.
        menuPortalTarget={null}
        onChange={(option) => {
          setLocalValue(option)

          if (applyOnChange) {
            updateSearch(schema.accessor, option)
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()

            handleSetOpen(false)
          }
        }}
        {...props}
      />
    </FilterTypePopup>
  )
}
